// ./src/blockFonts.ts — a block's own FONT and TEXT SIZE (ROADMAP A2e #84).
//
// A block's style section offers a font family and a text size on top of the theme. Both are stored in the block's
// config as KEYS (`fontKey`, `textSize`), never as CSS: a stored key the renderer does not know is simply ignored, so
// nothing a host (or a hand-edited config) saves can inject a declaration, and a font can be re-hosted or renamed
// without touching saved blocks.
//
// ⚠⚠ THE FONTS ARE SELF-HOSTED (owner, 2026-10-02: "self host around 20-30 most popular fonts for all the occasions …
// then dynamically decide which ones to load"). The files are Google Fonts (OFL / Apache) fetched ONCE by
// `packages/blocks/scripts/fetchBlockFonts.ts` into `apps/events-web/public/fonts/blocks/` and
// `apps/app/public/fonts/blocks/` (committed), and their @font-face rules are generated into
// `blockFontFaces.generated.ts`. Nothing is loaded from a font CDN at run time.
// ⚠ "Dynamically" is two halves: an event page declares @font-face rules ONLY for the fonts its blocks use
//   (`blockFontKeysOf` → `blockFontFaceCss`), and a browser downloads a declared file only when text actually renders
//   in it — and, through `unicode-range`, only the scripts (Latin / Cyrillic) that text contains.
// ⓘ Every hosted stack ends in a system fallback of the same kind, so a slow or blocked file still reads as intended.
import type { CSSProperties } from 'react';

import { BLOCK_FONT_FACES, type BlockFontFace } from './blockFontFaces.generated';

export type BlockFontGroup = 'theme' | 'modern' | 'classic' | 'script' | 'fun' | 'handwritten' | 'spooky' | 'basic';

export interface BlockFont {
  /** What the block stores (`config.fontKey`). Stable — never rename a shipped key. */
  key: string;
  /** What the host sees in the picker. */
  label: string;
  group: BlockFontGroup;
  /** The CSS font-family value the block gets. */
  stack: string;
  /** Hosted (self-served) fonts: the Google Fonts family and the weights fetched. Absent for theme / basic fonts. */
  hosted?: { family: string; weights: number[] };
}

/** The picker's group headings, in order. */
export const BLOCK_FONT_GROUPS: { group: BlockFontGroup; label: string }[] = [
  { group: 'theme', label: 'From the theme' },
  { group: 'modern', label: 'Modern' },
  { group: 'classic', label: 'Classic & elegant' },
  { group: 'script', label: 'Script & wedding' },
  { group: 'fun', label: 'Fun & party' },
  { group: 'handwritten', label: 'Handwritten' },
  { group: 'spooky', label: 'Spooky' },
  { group: 'basic', label: 'Basic' },
];

const SANS = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const SERIF = "Georgia, 'Times New Roman', serif";
const SCRIPT = "'Brush Script MT', 'Segoe Script', cursive";
const HAND = "'Segoe Print', 'Bradley Hand', cursive";

const hosted = (key: string, family: string, group: BlockFontGroup, fallback: string, weights: number[] = [400, 700]): BlockFont => ({
  key,
  label: family,
  group,
  stack: `'${family}', ${fallback}`,
  hosted: { family, weights },
});

/** Every font a block can choose, in picker order. The DEFAULT (no key) is the theme's text font. */
export const BLOCK_FONTS: BlockFont[] = [
  // ⚠ The theme's own heading font, re-pointed — never `--font-display` itself (that would be a cycle on the wrapper).
  // ⓘ Falls back to Mantine's heading font, so the editor (which has no event theme) previews a heading face too.
  { key: 'theme-heading', label: 'Theme heading font', group: 'theme', stack: 'var(--font-display, var(--mantine-font-family-headings, inherit))' },

  hosted('inter', 'Inter', 'modern', SANS),
  hosted('montserrat', 'Montserrat', 'modern', SANS),
  hosted('poppins', 'Poppins', 'modern', SANS),
  hosted('lato', 'Lato', 'modern', SANS),
  hosted('raleway', 'Raleway', 'modern', SANS),
  hosted('nunito', 'Nunito', 'modern', SANS),

  hosted('playfair-display', 'Playfair Display', 'classic', SERIF),
  hosted('lora', 'Lora', 'classic', SERIF),
  hosted('merriweather', 'Merriweather', 'classic', SERIF),
  hosted('cormorant-garamond', 'Cormorant Garamond', 'classic', SERIF),
  hosted('eb-garamond', 'EB Garamond', 'classic', SERIF),
  hosted('cinzel', 'Cinzel', 'classic', SERIF),

  hosted('great-vibes', 'Great Vibes', 'script', SCRIPT, [400]),
  hosted('dancing-script', 'Dancing Script', 'script', SCRIPT),
  hosted('parisienne', 'Parisienne', 'script', SCRIPT, [400]),
  hosted('allura', 'Allura', 'script', SCRIPT, [400]),
  hosted('sacramento', 'Sacramento', 'script', SCRIPT, [400]),
  hosted('pinyon-script', 'Pinyon Script', 'script', SCRIPT, [400]),

  hosted('pacifico', 'Pacifico', 'fun', SCRIPT, [400]),
  hosted('lobster', 'Lobster', 'fun', SCRIPT, [400]),
  hosted('fredoka', 'Fredoka', 'fun', SANS),
  hosted('bebas-neue', 'Bebas Neue', 'fun', SANS, [400]),
  hosted('righteous', 'Righteous', 'fun', SANS, [400]),
  hosted('comfortaa', 'Comfortaa', 'fun', SANS),

  hosted('caveat', 'Caveat', 'handwritten', HAND),
  hosted('amatic-sc', 'Amatic SC', 'handwritten', HAND),
  hosted('permanent-marker', 'Permanent Marker', 'handwritten', HAND, [400]),
  hosted('indie-flower', 'Indie Flower', 'handwritten', HAND, [400]),

  hosted('creepster', 'Creepster', 'spooky', SANS, [400]),
  hosted('nosifer', 'Nosifer', 'spooky', SANS, [400]),
  hosted('eater', 'Eater', 'spooky', SANS, [400]),

  { key: 'system-sans', label: 'System sans-serif', group: 'basic', stack: SANS },
  { key: 'system-serif', label: 'System serif', group: 'basic', stack: SERIF },
  { key: 'monospace', label: 'Monospace', group: 'basic', stack: "ui-monospace, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace" },
];

const BY_KEY = new Map(BLOCK_FONTS.map((f) => [f.key, f]));

/** The editor's font picker: the fonts under their group headings, in catalog order (empty groups left out). */
export const blockFontSelectData = (): { group: string; items: { value: string; label: string }[] }[] =>
  BLOCK_FONT_GROUPS.map((g) => ({
    group: g.label,
    items: BLOCK_FONTS.filter((f) => f.group === g.group).map((f) => ({ value: f.key, label: f.label })),
  })).filter((g) => g.items.length > 0);

/** The font a stored key names; null for no key or an unknown one (ignored, so the block keeps the theme's font). */
export const blockFontOf = (key: string | null | undefined): BlockFont | null => (key ? BY_KEY.get(key) ?? null : null);

/**
 * Display faces — lovely in a title, unreadable in a form field. A block in one of these keeps the theme's font in its
 * CONTROLS (inputs, buttons, selects read `--mantine-font-family`), so an RSVP or contact field is never in Nosifer.
 */
export const DECORATIVE_GROUPS: BlockFontGroup[] = ['script', 'fun', 'handwritten', 'spooky'];

/** Is this a display face that must stay out of the block's controls? (The wrapper gets `data-block-font-decorative`.) */
export const blockFontIsDecorative = (key: string | null | undefined): boolean => {
  const font = blockFontOf(key);

  return !!font && DECORATIVE_GROUPS.includes(font.group);
};

/**
 * Buttons and badges INHERIT the block's font (only inputs read `--mantine-font-family`), so under a decorative face
 * this puts every control back on the theme's font. Shipped with the font rules (`blockFontFaceCss`), which every page
 * using a decorative font — always a hosted one — declares.
 */
export const BLOCK_DECORATIVE_CONTROLS_CSS =
  '.em-block[data-block-font-decorative] :where(button, input, textarea, select, .mantine-Button-root, .mantine-Badge-root, .mantine-Pill-root, .mantine-SegmentedControl-root)' +
  '{font-family:var(--mantine-font-family);font-synthesis-weight:auto;}';

/**
 * The CSS a block's wrapper gets for its font: the family itself (text inherits it), `--mantine-font-family-headings`
 * for titles, `--font-body` / `--font-display` for `T.fontBody` / `T.fontDisplay`, and — except for a decorative font —
 * `--mantine-font-family`, which Mantine's buttons and inputs read.
 * ⚠ A hosted font with no bold face gets `font-synthesis-weight: none`: titles and buttons ask for 600–700, and a
 * browser SMEARS a script face into a synthetic bold. They render at the weight the font has instead.
 */
export const blockFontVars = (key: string | null | undefined): CSSProperties | null => {
  const font = blockFontOf(key);

  if (!font) {
    return null;
  }

  const vars: Record<string, string> = {
    fontFamily: font.stack,
    '--font-body': font.stack,
  };

  // `theme-heading` READS --font-display and --mantine-font-family-headings, so it must not also SET them on the same
  // element (a variable that refers to itself is a cycle, and the declaration is dropped). They are its value anyway.
  if (font.key !== 'theme-heading') {
    vars['--font-display'] = font.stack;
    vars['--mantine-font-family-headings'] = font.stack;
  }

  // ⓘ Nor `theme-heading`: a theme's heading face may itself be a display face, and the controls keep the body font.
  if (!DECORATIVE_GROUPS.includes(font.group) && font.key !== 'theme-heading') {
    vars['--mantine-font-family'] = font.stack;
  }

  if (font.hosted && !font.hosted.weights.includes(700)) {
    vars.fontSynthesisWeight = 'none';
  }

  return vars as CSSProperties;
};

// ── Text size ────────────────────────────────────────────────────────────────────────────────────────────────────

export interface BlockTextSize {
  /** What the block stores (`config.textSize`); the default stores nothing. */
  key: string;
  label: string;
  /** × the theme's sizes. */
  scale: number;
}

export const BLOCK_TEXT_SIZES: BlockTextSize[] = [
  { key: 'sm', label: 'Small', scale: 0.875 },
  { key: 'md', label: 'Default', scale: 1 },
  { key: 'lg', label: 'Large', scale: 1.125 },
  { key: 'xl', label: 'Extra large', scale: 1.25 },
  { key: 'xxl', label: 'Huge', scale: 1.5 },
];

/** The scale a stored key names; 1 for none or an unknown key. */
export const blockTextScale = (key: string | null | undefined): number => BLOCK_TEXT_SIZES.find((s) => s.key === key)?.scale ?? 1;

/** Mantine's own text sizes, as its styles.css declares them on `:root` (each × `--mantine-scale`). */
const MANTINE_TEXT_REM: Record<string, number> = { xs: 0.75, sm: 0.875, md: 1, lg: 1.125, xl: 1.25 };

/**
 * A block's TITLE (its <h2>), in rem × the block's scale. 2026-10-24 (owner: "yes, fix the sizes now") — it was the
 * xl text size (20px), smaller than the H2 (32px) and H3 (24px) a host writes inside a text block.
 */
export const BLOCK_TITLE_REM = 1.5;

/**
 * A heading's size INSIDE a block: its rem × the block's Text size (`--mantine-scale`) × the theme's heading size
 * (`--em-heading-scale`, `font.headingSize`, 2026-10-25). The ONE formula for the block title, the headings in its text
 * and its cards' titles — so a theme's Large headings and a block's Small text compose everywhere alike.
 */
export const blockHeadingSize = (rem: number): string => `calc(${rem}rem * var(--mantine-scale) * var(--em-heading-scale, 1))`;

/**
 * The headings INSIDE a block — a host's rich text, a view's own `Title` — sit UNDER the block's title (24px): H1 and
 * H2 at 20px, H3 at 18px, H4 and H5 at the body's 16px, H6 at 14px. Only the size: the saved text keeps its tags.
 * ⓘ These replace the page's `--mantine-hN-font-size` (Mantine's / the brand's, 40px for an H1) on every block, at
 *   every text size — so they are declared on the wrapper always, × `--mantine-scale` (the block's own, else :root's 1).
 */
const BLOCK_HEADING_REM: Record<string, number> = { h1: 1.25, h2: 1.25, h3: 1.125, h4: 1, h5: 1, h6: 0.875 };

export const BLOCK_HEADING_VARS: CSSProperties = Object.fromEntries(
  Object.entries(BLOCK_HEADING_REM).map(([h, rem]) => [`--mantine-${h}-font-size`, blockHeadingSize(rem)]),
) as CSSProperties;

/**
 * The CSS a block's wrapper gets for its text size. Null at the default size.
 *
 * ⚠⚠ IT SETS `--mantine-scale` ON THE WRAPPER. Everything Mantine sizes in rem is `calc(Nrem * var(--mantine-scale))`,
 * resolved on the element that declares it — so inside the block a view's own numeric size (`fz={42}` on a stat),
 * a badge, a button's and an input's height all grow with the text, instead of a "Huge" block with big labels, small
 * headline numbers and cramped fields. The text size VARIABLES (`--mantine-font-size-*`) are
 * declared on `:root` and arrive already resolved, so they are re-declared here with Mantine's own values, to be
 * resolved again against the block's scale.
 * ⚠ Assumes the page's own `--mantine-scale` is 1 (events-web and the app both leave Mantine's default); a theme that
 *   ever sets one would need multiplying in here — a variable cannot refer to itself.
 * ⓘ Not scaled: the spacing scale (gaps, also resolved on :root), pixel sizes in plain `style={{}}`, and font sizes a
 *   host set inside rich text. ⓘ The heading sizes are `BLOCK_HEADING_VARS`, on every block whatever its size.
 */
export const blockTextSizeVars = (key: string | null | undefined): CSSProperties | null => {
  const scale = blockTextScale(key);

  if (scale === 1) {
    return null;
  }

  const vars: Record<string, string> = {
    '--mantine-scale': String(scale),
    '--em-block-text-scale': String(scale),
    fontSize: 'calc(1rem * var(--mantine-scale))',
  };

  for (const [size, rem] of Object.entries(MANTINE_TEXT_REM)) {
    vars[`--mantine-font-size-${size}`] = `calc(${rem}rem * var(--mantine-scale))`;
  }

  return vars as CSSProperties;
};

// ── Loading ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** Where the font files are served from on both apps (`public/fonts/blocks/`). */
export const BLOCK_FONTS_PATH = '/fonts/blocks';

/** The hosted fonts a set of blocks uses — what a page must declare. Unknown and non-hosted keys are left out. */
export const blockFontKeysOf = (widgets: { config?: unknown }[] | null | undefined): string[] => {
  const keys = new Set<string>();

  for (const w of widgets ?? []) {
    const stored = (w?.config as { fontKey?: unknown } | null | undefined)?.fontKey;
    const font = blockFontOf(typeof stored === 'string' ? stored : null);

    if (font?.hosted) {
      keys.add(font.key);
    }
  }

  return [...keys].sort();
};

/** Every hosted font — the editor declares them all (its picker previews each one). */
export const ALL_HOSTED_BLOCK_FONT_KEYS: string[] = BLOCK_FONTS.filter((f) => !!f.hosted).map((f) => f.key);

const faceRule = (family: string, face: BlockFontFace): string =>
  `@font-face{font-family:'${family}';font-style:normal;font-weight:${face.weight};font-display:swap;` +
  `src:url(${BLOCK_FONTS_PATH}/${face.file}) format('woff2');unicode-range:${face.unicodeRange};}`;

/** The @font-face rules for these fonts (and nothing else), plus the controls rule when one is decorative. */
export const blockFontFaceCss = (keys: string[], faces: Record<string, BlockFontFace[]> = BLOCK_FONT_FACES): string => {
  const rules = keys
    .map((key) => {
      const font = blockFontOf(key);

      if (!font?.hosted) {
        return '';
      }

      return (faces[key] ?? []).map((face) => faceRule(font.hosted!.family, face)).join('\n');
    })
    .filter(Boolean);

  if (rules.length > 0 && keys.some((k) => blockFontIsDecorative(k))) {
    rules.push(BLOCK_DECORATIVE_CONTROLS_CSS);
  }

  return rules.join('\n');
};

// ── Theme fonts (ROADMAP A2e #89) ───────────────────────────────────────────────────────────────────────────────────
//
// A THEME's heading and text faces (`tokens.font.display` / `font.body`) are CSS stacks — the token contract the site
// has always read. The theme editor offers the same hosted faces a block can pick (owner, 2026-10-03; #84 call (d)) plus
// the system stacks the seeded themes use, and stores the chosen option's STACK. The site recognises a hosted face by
// its exact stack (`themeFontKeysOf`) and declares only those files, so — as for a block — no stored value ever reaches
// the @font-face CSS. ⓘ The server checks a stack is names and commas only (eventsmagic-api `boundary/theme.ts`).

/**
 * The system stacks the seeded themes use. ⚠ EXACT copies of eventsmagic-api `src/database/seeds/objects/theme.js` `F`,
 * so a duplicated default theme's fonts are recognised by the picker (`check-theme-editor` compares the two).
 */
export const THEME_SYSTEM_FONTS: { key: string; label: string; stack: string }[] = [
  { key: 'theme-serif-warm', label: 'Warm serif', stack: "'Iowan Old Style','Palatino Linotype',Palatino,Georgia,serif" },
  { key: 'theme-serif-elegant', label: 'Elegant serif', stack: "'Hoefler Text','Baskerville','Big Caslon',Georgia,serif" },
  { key: 'theme-sans', label: 'Clean sans-serif', stack: "system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif" },
  { key: 'theme-geometric', label: 'Geometric', stack: "'Space Grotesk',system-ui,'Segoe UI',sans-serif" },
  { key: 'theme-rounded', label: 'Rounded', stack: "'SF Pro Rounded','Hiragino Maru Gothic ProN',system-ui,sans-serif" },
];

export type ThemeFontRole = 'display' | 'body';

export interface ThemeFontOption {
  key: string;
  label: string;
  stack: string;
  /** Hosted faces only — the files a page declares. */
  hosted?: { family: string; weights: number[] };
}

/** Every face a theme can use, keyed — the system stacks, then the block catalog (less its theme-relative entry). */
const THEME_FONTS: ThemeFontOption[] = [
  ...THEME_SYSTEM_FONTS,
  ...BLOCK_FONTS.filter((f) => f.group !== 'theme').map((f) => ({ key: f.key, label: f.label, stack: f.stack, hosted: f.hosted })),
];

const THEME_FONT_BY_KEY = new Map(THEME_FONTS.map((f) => [f.key, f]));
const THEME_FONT_BY_STACK = new Map(THEME_FONTS.map((f) => [f.stack, f]));

/**
 * The theme editor's picker for one role, under group headings. ⚠ The TEXT face leaves out the display groups (script,
 * fun, handwritten, spooky): a whole site — its forms and its buttons — in Nosifer is unreadable. Values are keys; the
 * editor stores `themeFontOf(key).stack`.
 */
export const themeFontSelectData = (role: ThemeFontRole): { group: string; items: { value: string; label: string }[] }[] => [
  { group: 'Theme fonts (load instantly)', items: THEME_SYSTEM_FONTS.map((f) => ({ value: f.key, label: f.label })) },
  ...BLOCK_FONT_GROUPS.filter((g) => g.group !== 'theme' && (role === 'display' || !DECORATIVE_GROUPS.includes(g.group)))
    .map((g) => ({
      group: g.label,
      items: BLOCK_FONTS.filter((f) => f.group === g.group).map((f) => ({ value: f.key, label: f.label })),
    }))
    .filter((g) => g.items.length > 0),
];

/** The face a picker key names, or null. */
export const themeFontOf = (key: string | null | undefined): ThemeFontOption | null => (key ? THEME_FONT_BY_KEY.get(key) ?? null : null);

/** The picker key of a stored stack, or null for a stack that is not in the catalog (the editor then shows it as is). */
export const themeFontKeyOfStack = (stack: string | null | undefined): string | null =>
  (stack ? THEME_FONT_BY_STACK.get(stack.trim())?.key : null) ?? null;

/** What the theme font helpers read: the heading and text stacks, and the event title's (A2e #89). */
export interface ThemeFontTokens {
  font?: { display?: string | null; body?: string | null } | null;
  element?: { title?: { family?: string | null } | null } | Record<string, any> | null;
}

const titleStackOf = (tokens: ThemeFontTokens | null | undefined): string | undefined => {
  const family = (tokens?.element as { title?: { family?: unknown } } | null | undefined)?.title?.family;

  return typeof family === 'string' ? family : undefined;
};

/** The HOSTED faces a theme's stacks name (heading, text, event title) — what a themed page must declare. */
export const themeFontKeysOf = (tokens: ThemeFontTokens | null | undefined): string[] => {
  const keys = new Set<string>();

  [tokens?.font?.display, tokens?.font?.body, titleStackOf(tokens)].forEach((stack) => {
    const font = typeof stack === 'string' ? THEME_FONT_BY_STACK.get(stack.trim()) : undefined;

    if (font?.hosted) {
      keys.add(font.key);
    }
  });

  return [...keys].sort();
};

/**
 * The CSS a themed subtree needs for its fonts: the @font-face rules of the hosted faces its theme names, and — when the
 * HEADING face has no bold file — `font-synthesis-weight: none` on headings under `scope`, so a script title renders at
 * the weight it has rather than smeared into a synthetic bold (the block rule, `blockFontVars`, for the whole site).
 */
export const themeFontFaceCss = (tokens: ThemeFontTokens | null | undefined, scope: string): string => {
  const rules = [blockFontFaceCss(themeFontKeysOf(tokens))];
  const display = typeof tokens?.font?.display === 'string' ? THEME_FONT_BY_STACK.get(tokens.font.display.trim()) : undefined;
  const title = titleStackOf(tokens) ? THEME_FONT_BY_STACK.get(titleStackOf(tokens)!.trim()) : undefined;

  if (display?.hosted && !display.hosted.weights.includes(700)) {
    rules.push(`${scope} :is(h1,h2,h3,h4,h5,h6){font-synthesis-weight:none;}`);
  }

  // The event title and the header's event name (`.em-event-hero__title`, `.em-event-header__brand` — both in the class
  // contract, both bold) in a face with no bold file.
  if (title?.hosted && !title.hosted.weights.includes(700)) {
    rules.push(`${scope} :is(.em-event-hero__title,.em-event-header__brand){font-synthesis-weight:none;}`);
  }

  return rules.filter(Boolean).join('\n');
};
